import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Title, Button, Group, Stack, Modal } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import { Caption, CaptionResponse, ModalData } from '../../types';
import VideoTimeline from '../VideoTimeline/VideoTimeline';
import EditSegmentTabs from '../RightTabList/EditSegmentTabs';
import Navigation from '../Home/Navigation';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import styles from './CaptionEditor.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faArrowLeft, faPencilAlt } from '@fortawesome/free-solid-svg-icons'; 
import { downloadVideo as autoDownloadVideo } from '../../services/autoDownloadService';
import { downloadVideo } from '../../services/api';
import { getApiUrl } from '../../config/api';
import { fetchCaptionsFromS3, saveCaptionsToS3 } from '../VideoEditor/captionService';
import loaderStyles from '../../assets/loader.module.css';
import VerticalNav from '../Home/VerticalNav';
import { useNotification } from '../../context/NotificationContext';
import { useTitleGeneration } from '../../context/TitleGenerationContext';
import { useThumbnailModal } from '../../context/ThumbnailContext';
import { StepIndicatorNames } from '../../types';
import { projectService } from '../../services/projectService';
import { useAuth } from '../../context/Auth';
import { useAspectRatio } from '../../hooks/useAspectRatio';
import { getSupportedRatios } from '../../services/imageGenerationApi';
import { useCaptionEditor } from '../../hooks';
import { CaptionEditorControls } from './components';
import { 
  separateCaptionsAndTitles, 
  combineCaptionsAndTitles, 
  refetchCaptionsWithWordCount,
  applyCaptionDrag
} from '../../utils/captionUtils';
import DescriptionEditor from '../VideoEditor/DescriptionEditor';
import { useMobileWarning } from '../../hooks/useMobileWarning';
import MobileWarningModal from '../EnhancedHome/MobileWarningModal';

const CaptionEditor: React.FC = () => {
  // Mobile warning hook
  const { showMobileWarning, handleMobileWarningClose, handleMobileWarningContinue } = useMobileWarning();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('captionEditor');
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin';
  
  // Use the caption editor hook for all caption/title state
  const {
    editMode,
    setEditMode,
    captions,
    titles,
    currentItems,
    setCaptions,
    setTitles,
    setCurrentItems,
    applyToAllCaptions,
    setApplyToAllCaptions,
    excludeCaptions,
    setExcludeCaptions,
    excludeTitles,
    setExcludeTitles,
    maxWordsPerCaption,
    setMaxWordsPerCaption,
    getAllItems,
    getFilteredItemsForExport
  } = useCaptionEditor({
    initialCaptions: [],
    initialTitles: [],
    initialMode: 'captions'
  });
  
  // Global state for caption editing
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [video, setVideo] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<{ 
    fetchingCaption: boolean, 
    downloadCaptionVideo: boolean,
    savingVideo: boolean 
  }>({
    fetchingCaption: false, 
    downloadCaptionVideo: false,
    savingVideo: false 
  });
  const [captionedVideoUrl, setCaptionedVideoUrl] = useState<string | null>(null);
  const [projectTitle, setProjectTitle] = useState<string>("Untitled Project");
  const [projectDescription, setProjectDescription] = useState<string>("");
  const [projectData, setProjectData] = useState<any>(null);
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [hasExistingCaptions, setHasExistingCaptions] = useState<boolean>(false);
  const [aspectRatio, setAspectRatio] = useState<string | null>('9:16'); // Default to 9:16 (vertical video)
  const [isRefetchingCaptions, setIsRefetchingCaptions] = useState(false);
  
  // Refs - declare before any useEffect that uses them
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileRef = useRef<File | null>(null);
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [searchParams] = useSearchParams();
  let projectId = useRef("");
  let downloadLink = useRef("");
  let refLinkDownload = useRef<HTMLAnchorElement>(null);
  let saveLabel = useRef("Save");
  const [disabledBtnPendingDownload, setDisabledBtnPendingDownload] = useState<boolean>(false);
  
  // Detect aspect ratio from video metadata
  useEffect(() => {
    if (videoRef.current && video) {
      const videoElement = videoRef.current;
      
      const detectRatio = () => {
        const width = videoElement.videoWidth;
        const height = videoElement.videoHeight;
        
        if (width && height) {
          const ratio = width / height;
          let detectedRatio = '';
          
          // Detect common aspect ratios
          if (Math.abs(ratio - 16/9) < 0.1) {
            detectedRatio = '16:9';
          } else if (Math.abs(ratio - 9/16) < 0.1) {
            detectedRatio = '9:16';
          } else if (Math.abs(ratio - 1) < 0.1) {
            detectedRatio = '1:1';
          } else if (Math.abs(ratio - 4/3) < 0.1) {
            detectedRatio = '4:3';
          } else if (Math.abs(ratio - 21/9) < 0.1) {
            detectedRatio = '21:9';
          } else {
            // Custom ratio
            detectedRatio = `${width}:${height}`;
          }
          
          console.log('Video dimensions:', width, 'x', height, '-> Aspect ratio:', detectedRatio, '(ratio:', ratio.toFixed(4), ')');
          
          // Only set if changed to prevent re-renders
          setAspectRatio(prev => prev !== detectedRatio ? detectedRatio : prev);
        }
      };
      
      // Try to detect immediately if metadata is already loaded
      if (videoElement.videoWidth > 0) {
        detectRatio();
      }
      
      // Also listen for loadedmetadata event
      const handleLoadedMetadata = () => {
        console.log('Video metadata loaded');
        detectRatio();
      };
      
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      
      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [video]);
  
  // Watermark state - checked by default for free users, unchecked for paid users
  const isFreePlan = user?.subscriptionPlan === 'Creator (Free)';
  const [includeWatermark, setIncludeWatermark] = useState<boolean>(isFreePlan);

  // Sync watermark state with user plan changes
  useEffect(() => {
    setIncludeWatermark(isFreePlan);
  }, [isFreePlan]);

  /**
   * Creates a watermark caption that appears throughout the entire video
   */
  const createWatermarkCaption = useCallback((): Caption => {
    return {
      id: 'watermark-' + Date.now(),
      start: 0,
      end: duration || 9999, // Cover entire video duration
      text: 'KingdomStudio.pro',
      style: {
        fontSize: 30,
        color: '#ffffff40', // Semi-transparent white (25% opacity)
        stroke: '#00000040', // Semi-transparent black stroke (25% opacity)
        strokeWidth: 1,
        fontFamily: 'Anton',
        shadowColor: '#00000000', // Fully transparent shadow (no shadow)
        shadowxz: 0,
        specialEffect: undefined,
        yPosition: 6.977886977886977,
        xPosition: 82.45865635359161,
        textAlign: 'center',
      },
      itemType: 'watermark',
      isWatermark: true
    };
  }, [duration]);

  // Create display items that include watermark if enabled
  // Watermark appears in video display but NOT on timeline
  const displayItems = useMemo(() => {
    if (!includeWatermark || !duration) {
      return currentItems;
    }
    
    const watermarkCaption = createWatermarkCaption();
    return [...currentItems, watermarkCaption];
  }, [currentItems, includeWatermark, duration, createWatermarkCaption]);

  // Handle caption dragging using utility function
  const handleCaptionDrag = useCallback((captionId: string, newYPosition: number, newXPosition?: number) => {
    // Don't allow dragging watermark
    if (captionId.startsWith('watermark-')) return;
    
    const updatedItems = applyCaptionDrag(
      currentItems,
      captionId,
      newYPosition,
      newXPosition,
      applyToAllCaptions
    );
    setCurrentItems(updatedItems);
  }, [currentItems, applyToAllCaptions, setCurrentItems]);

  // Handle caption click to open editor tab
  const handleCaptionClick = useCallback((captionId: string) => {
    // Don't allow clicking watermark
    if (captionId.startsWith('watermark-')) return;
    
    const clickedCaption = currentItems.find(item => item.id === captionId);
    if (clickedCaption) {
      setModalData({
        isOpen: true,
        segment: clickedCaption,
        targetId: captionId,
        onClose: () => setModalData(null)
      });
    }
  }, [currentItems]);

  const { addNotification } = useNotification();
  const { openTitleModal } = useTitleGeneration();
  const { openThumbnailModal } = useThumbnailModal();
  
  // Format time helper function
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Debug logs for state updates
  useEffect(() => {
    console.log("currentTime updated:", currentTime);
  }, [currentTime]);

  useEffect(() => {
    console.log("downloadLink updated:", downloadLink.current);
  }, [downloadLink.current]);

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Use a ref to ensure the effect runs only once.
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    console.log("URL Parameters:", Object.fromEntries(searchParams.entries()));
    
    const videoUrl = searchParams.get('video-url');
    projectId.current = searchParams.get('project-id') ?? "";
    
    console.log("Extracted video-url:", videoUrl);
    console.log("Extracted project-id:", projectId.current);
    
    if(videoUrl && projectId.current){
      setVideo(videoUrl);
      console.log("Video Loaded:", videoUrl); 
    }

    // If we have a project ID, fetch the project details to get the captionedVideoUrl
    if(projectId.current) {
      fetchProjectDetails(projectId.current).then((projectData) => {
        if(projectData && projectData.captionedVideoUrl) {
          console.log("Found captionedVideoUrl from project:", projectData.captionedVideoUrl);
          setCaptionedVideoUrl(projectData.captionedVideoUrl);
          downloadLink.current = projectData.captionedVideoUrl;
        }
      }).catch((error) => {
        console.error("Error fetching project details:", error);
      });
    }

    // Load captions for this project
    if(projectId.current && videoUrl){
      setDisabledBtnPendingDownload(true);
      fetchCaptionsFromS3(projectId.current).then((data: Caption[]) => {
        console.log("Captions fetched successfully");
        
        // Separate captions and titles based on itemType using utility
        const { captions: loadedCaptions, titles: loadedTitles } = separateCaptionsAndTitles(data);
        
        setCaptions(loadedCaptions);
        setTitles(loadedTitles);
        setHasExistingCaptions(data.length > 0);
        //addNotification('success', t('captionEditor.notifications.success.captionsLoaded', { captionCount: loadedCaptions.length, titleCount: loadedTitles.length }));
      }).catch((error: Error) => {
        console.error("Error fetching captions:", error);
        setHasExistingCaptions(false);
        addNotification('error', t('captionEditor.notifications.error.loadCaptions', { error: error.message }));
      }).finally(() => { setDisabledBtnPendingDownload(false) });
    }

  }, [searchParams]);

  // ✅ FIX: Update projectData whenever captionedVideoUrl changes so VerticalNav sees the latest captioned video
  useEffect(() => {
    if (captionedVideoUrl && projectData) {
      setProjectData((prev: any) => ({
        ...prev,
        captionedVideoUrl: captionedVideoUrl
      }));
      console.log('✅ ProjectData updated with new captionedVideoUrl:', captionedVideoUrl);
    }
  }, [captionedVideoUrl]);

  const handleSaveCaptionsToS3 = async (projectId: string, itemsToSave: Caption[], itemType: 'captions' | 'titles') => {
    setDisabledBtnPendingDownload(true);
    setIsProcessing((prev) => ({ ...prev, savingVideo: true }));
    saveLabel.current = "Saved";
    
    try {
      console.log(`Saving all items (captions + titles) to database and S3`);
      
      // Combine both captions and titles with itemType identifiers using utility
      const allItems = combineCaptionsAndTitles(captions, titles);
      
      const result = await saveCaptionsToS3(projectId, allItems);
      console.log(`All items saved successfully:`, result);
      //addNotification('success', t('captionEditor.notifications.success.captionsSaved'));
    }
    catch (error) {
      console.error(`Error saving items:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addNotification('error', t('captionEditor.notifications.error.saveCaptions', { error: errorMessage }));
    }
    finally {
      setDisabledBtnPendingDownload(false);
      setIsProcessing((prev) => ({ ...prev, savingVideo: false }));
    }
  }

  /** VIDEO UPLOAD & CAPTIONS **/
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setVideo(url);
      fileRef.current = file;
      console.log("Uploaded video:", url);
    }
  };

  // When the timeline is clicked or a seek event occurs.
  const handleSeek = (time: number) => {
    console.log("Timeline seek clicked. New time:", time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      console.log("Video element updated to:", time);
    }
    setCurrentTime(time);
  };

  const fetchCaptions = async (forceRegenerate = false) => {
    if (!video) {
      alert(t('captionEditor.notifications.error.uploadVideoFirst'));
      return;
    }
    setIsProcessing((prev) => ({ ...prev, fetchingCaption: true }));
    setDisabledBtnPendingDownload(true);
    
    try {
      // Only check for existing captions if not forcing regeneration
      if (!forceRegenerate && projectId.current) {
        try {
          console.log("Checking for existing captions in database");
          const existingCaptions = await fetchCaptionsFromS3(projectId.current);
          
          if (existingCaptions && existingCaptions.length > 0) {
            console.log("Found existing captions in database, using them");
            
            // Separate captions and titles based on itemType
            const loadedCaptions = existingCaptions.filter(item => item.itemType === 'captions' || !item.itemType).map((caption, index) => ({
              ...caption, 
              id: (index + 1).toString()
            }));
            
            const loadedTitles = existingCaptions.filter(item => item.itemType === 'titles').map((title, index) => ({
              ...title, 
              id: (index + 1).toString()
            }));
            
            setCaptions(loadedCaptions);
            setTitles(loadedTitles);
            setHasExistingCaptions(existingCaptions.length > 0);
            //addNotification('success', `Loaded ${loadedCaptions.length} captions and ${loadedTitles.length} titles from database!`);
            return;
          }
        } catch (error) {
          console.log("No existing captions found, generating new ones");
        }
      }
      
      // Generate new captions (either no existing captions or forcing regeneration)
      if (forceRegenerate) {
        console.log("Force regenerating captions - skipping existing check");
      }
      
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(getApiUrl('/video/generate-captions'), {
        method: 'POST',
        headers,
        body: JSON.stringify({ video, projectId: projectId.current }),
      });
      
      if (!response.ok) throw new Error('Failed to fetch captions');
      
      const responseData: CaptionResponse = await response.json();
      
      // If we get back a project ID, store it
      if (responseData.projectId && (!projectId.current || projectId.current !== responseData.projectId)) {
        console.log(`Setting project ID from caption generation response: ${responseData.projectId}`);
        projectId.current = responseData.projectId;
        
        // Update URL with new project ID if needed
        if (searchParams.get('project-id') !== projectId.current) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('project-id', projectId.current);
          navigate({ search: newParams.toString() }, { replace: true });
        }
      }
      
      const newItems = responseData.data.map((caption, index) => ({
        ...caption, 
        id: (index + 1).toString(),
        itemType: editMode // Tag with current mode ('captions' or 'titles')
      }));
      
      // Update the correct array based on edit mode
      if (editMode === 'captions') {
        setCaptions(newItems);
      } else {
        setTitles(newItems);
      }
      
      setHasExistingCaptions(true);
      console.log(`Generated new ${editMode}:`, responseData.data);
      
      const successMessage = forceRegenerate ? 
        t('captionEditor.notifications.success.captionsRegenerated') : 
        t('captionEditor.notifications.success.captionsGenerated');
      addNotification('success', successMessage);
    } catch (error) {
      console.error('Error with captions:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addNotification('error', t('captionEditor.notifications.error.generateCaptions', { error: errorMessage }));
    } finally {
      setIsProcessing((prev) => ({ ...prev, fetchingCaption: false }));
      setDisabledBtnPendingDownload(false);
    }
  };

  // Refetch captions with word count parameter using utility
  const handleRefetchCaptions = async () => {
    if (!projectId.current) {
      addNotification('error', t('captionEditor.notifications.error.noProjectId'));
      return;
    }
    
    setIsRefetchingCaptions(true);
    
    try {
      const token = localStorage.getItem('authToken');
      const reformattedCaptions = await refetchCaptionsWithWordCount(
        projectId.current,
        maxWordsPerCaption,
        token || undefined
      );
      
      // Update only captions (not titles) when in caption mode
      if (editMode === 'captions') {
        setCaptions(reformattedCaptions);
        addNotification('success', t('captionEditor.notifications.success.captionsReformatted', { wordCount: maxWordsPerCaption }));
      }
      
    } catch (error) {
      console.error('Error refetching captions:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addNotification('error', t('captionEditor.notifications.error.reformatCaptions', { error: errorMessage }));
    } finally {
      setIsRefetchingCaptions(false);
    }
  };

  // Updates time from VideoPlayer's playback.
  const handleTimeUpdate = (time: number) => {
    console.log("handleTimeUpdate called with time:", time);
    setCurrentTime(time);
  };

  const handleDurationChange = (newDuration: number) => {
    console.log("handleDurationChange called with duration:", newDuration);
    console.log("Setting duration state to:", newDuration); // DEBUG: Check duration being set
    setDuration(newDuration);
  };

  /** MODAL **/
  const getModalData = (modalVal: ModalData) => {
    console.log("Modal data received:", modalVal);
    setModalData({
      ...modalVal,
      onClose: () => setModalData(prev => (prev ? { ...prev, isOpen: false } : null)),
    });
  };

  /**
   * Handles saving the caption video
   */
  const handleSaveCaptionVideo = async (captionResponse: CaptionResponse) => {
    try {
      setIsProcessing((prev) => ({ ...prev, downloadCaptionVideo: true }));
      setDisabledBtnPendingDownload(true);
      console.time("downloadTime");
      console.log("Caption video generated at:", captionResponse.videoPath);

      console.timeEnd("downloadTime"); 

      const videoUrl = await downloadVideo(captionResponse);
      downloadLink.current = videoUrl;
      setCaptionedVideoUrl(videoUrl);
      console.log("Generated video URL:", videoUrl);
      
      await autoDownloadVideo(videoUrl, {
        filename: `captioned-video-${Date.now()}.mp4`
      });
      //addNotification('success', t('captionEditor.notifications.success.videoGenerated'));
      
      // Refetch project details to get updated setCaptionedVideoUrl
      fetchProjectDetails(projectId.current).then((projectData) => {
        if(projectData && projectData.captionedVideoUrl) {
          setCaptionedVideoUrl(projectData.captionedVideoUrl);
        }
      });
    } catch (error) {
      console.error("Error saving caption video:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addNotification('error', t('captionEditor.notifications.error.generateVideo', { error: errorMessage }));
      throw error;
    } finally {
      setIsProcessing((prev) => ({ ...prev, downloadCaptionVideo: false }));
      setDisabledBtnPendingDownload(false);
    }
  }

  const getCaptionId = (id: string, captions: Caption[]): number => {
    const found = captions.find(el => el.id === id);
    return found ? +found.id : 0;
  };

  // Handle updating the project title
  const handleUpdateTitle = async (newTitle: string) => {
    if (!projectId.current || !newTitle.trim()) return;
    
    try {
      setDisabledBtnPendingDownload(true);
      
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(getApiUrl(`/projects/${projectId.current}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ title: newTitle }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update project title');
      }
      
      setProjectTitle(newTitle);
      setIsEditingTitle(false);
      
      //addNotification('success', t('captionEditor.notifications.success.titleUpdated'));
    } catch (error) {
      console.error("Error updating project title:", error);
      addNotification('error', t('captionEditor.notifications.error.updateTitle'));
      fetchProjectDetails(projectId.current);
    } finally {
      setDisabledBtnPendingDownload(false);
    }
  };

  // Handle updating the project description
  const handleUpdateDescription = async (newDescription: string) => {
    if (!projectId.current) return;
    
    try {
      setDisabledBtnPendingDownload(true);
      
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(getApiUrl(`/projects/${projectId.current}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify({ description: newDescription }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update project description');
      }
      
      setProjectDescription(newDescription);
      //addNotification('success', t('captionEditor.notifications.success.descriptionUpdated'));
    } catch (error) {
      console.error("Error updating project description:", error);
      addNotification('error', t('captionEditor.notifications.error.updateDescription'));
      fetchProjectDetails(projectId.current);
    } finally {
      setDisabledBtnPendingDownload(false);
    }
  };

  const handleNavSelected = async (key: StepIndicatorNames) => {
    console.log("handleNavSelected:", key);
    
    // Handle navigation to other steps
    if (key === StepIndicatorNames.AudioGeneration) {
      const currentProjectId = projectId.current;
      if (currentProjectId) {
        try {
          const projectData = await fetchProjectDetails(currentProjectId);
          if (projectData) {
            const projectAudioUrl = projectData.finalAudio || projectData.originalAudioUrl;
            if (projectAudioUrl) {
              console.log('Found audio URL from project for audio editor:', projectAudioUrl);
              navigate(`/audio-editor/${currentProjectId}?audioUrl=${encodeURIComponent(projectAudioUrl)}`);
            } else {
              console.warn('No audio URL found in project data for caption project');
              // VerticalNav now handles this with its own modal logic
              return;
            }
          } else {
            console.warn('No project data found');
            return;
          }
        } catch (error) {
          console.error('Error fetching project details for audio editor:', error);
          return;
        }
      } else {
        console.warn('No project ID available');
        return;
      }
    }
    
    if (key === StepIndicatorNames.SoundGeneration) {
      const currentProjectId = projectId.current;
      if (currentProjectId) {
        navigate(`/sound-editor/${currentProjectId}`);
      } else {
        navigate('/sound-generation');
      }
    }
    
    else if (key === StepIndicatorNames.ContentCreation) {
      const currentProjectId = projectId.current;
      
      if (currentProjectId) {
        try {
          const projectData = await fetchProjectDetails(currentProjectId);
          if (projectData) {
            const audioUrl = projectData.finalAudio || projectData.originalAudioUrl;
            if (audioUrl) {
              console.log('Found audio URL from project:', audioUrl);
              window.location.href = `/caption?project-id=${currentProjectId}&audio-url=${encodeURIComponent(audioUrl)}`;
            } else {
              console.warn('No audio URL found in project data, navigating with project ID only');
              window.location.href = `/caption?project-id=${currentProjectId}`;
            }
          } else {
            console.warn('No project data found, navigating with project ID only');
            window.location.href = `/caption?project-id=${currentProjectId}`;
          }
        } catch (error) {
          console.error('Error fetching project details for audio URL:', error);
          window.location.href = `/caption?project-id=${currentProjectId}`;
        }
      } else {
        window.location.href = '/create-content';
      }
    }
    
    else if (key === StepIndicatorNames.TitleDescTag) {
      if (video) {
        openTitleModal('video', video, projectId.current);
      } else {
        openTitleModal('none', undefined, projectId.current);
      }
    } 
    
    else if (key === StepIndicatorNames.ThumbnailCreation) {
      if (video) {
        openThumbnailModal({
          projectId: projectId.current,
          mediaUrl: video,
          mediaType: 'video',
          hasExistingCaptions: captions.length > 0
        });
      } else {
        openThumbnailModal({
          projectId: projectId.current,
          mediaType: 'none',
          hasExistingCaptions: captions.length > 0
        });
      }
    }
    
    else if (key === StepIndicatorNames.ScriptGeneration) {
      const currentProjectId = projectId.current;
      if (currentProjectId) {
        navigate(`/script-generator/${currentProjectId}`);
      } else {
        navigate('/script-generator');
      }
    }
    
    else if (key === StepIndicatorNames.IdeaCreation) {
      const currentProjectId = projectId.current;
      if (currentProjectId) {
        navigate(`/prompt-generator/${currentProjectId}`);
      } else {
        navigate('/prompt-generator');
      }
    }
    
    else if (key === StepIndicatorNames.Export) {
      console.log('Export step selected');
    }
  }

  // Function to fetch project details
  const fetchProjectDetails = async (projectId: string) => {
    try {
      console.log("Fetching project details for projectId:", projectId);
      const projectData = await projectService.getProject(projectId);
      console.log("Project data retrieved:", projectData);
      
      if (projectData && projectData.title) {
        setProjectTitle(projectData.title);
      }

      if (projectData && projectData.description) {
        setProjectDescription(projectData.description);
      }
      
      // Store the project data for VerticalNav
      setProjectData(projectData);
      
      return projectData;
    } catch (error) {
      console.error("Error fetching project details:", error);
      
      try {
        const allProjects = await projectService.getProjects();
        console.log("Available projects for this user:", allProjects);
        console.log("Requested project ID:", projectId);
        console.log("Does project exist?", allProjects.some(p => p.id === projectId));
      } catch (listError) {
        console.error("Could not fetch user projects:", listError);
      }
      
      return null;
    }
  };

  const [navSelected, setNavSelected] = useState<StepIndicatorNames>(StepIndicatorNames.CaptionCreation);

  return (
    <div className={styles.container}>
      <Navigation />
      {/* Mobile Warning Modal */}
      <MobileWarningModal 
        show={showMobileWarning}
        onClose={handleMobileWarningClose}
        onContinue={handleMobileWarningContinue}
      />
      <VerticalNav 
        handleNavSelected={handleNavSelected} 
        initialStep={StepIndicatorNames.CaptionCreation}
        projectData={projectData}
      />
      <main className={styles.main}>
        <Group style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* left buttons */}
          <section>
            <div style={{ display: "flex", justifyContent: 'space-between', alignItems: "center", width: "100%" }}>
              <div style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "-0.50rem" }}>
                  <Button 
                    className={styles.backButton}
                    onClick={() => navigate('/Caption-LandingPage')}
                    title={t('captionEditor.tooltips.backButton')}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </Button>
                  
                  <h1 className={styles.pageTitle}>{t(`captionEditor.pageTitleMode.${editMode}`)}</h1>
                </div>
                
                {/* Project Title */}
                <div className={styles.titleContainer} style={{ marginLeft: "4rem" }}>
                  {isEditingTitle ? (
                    <input
                      type="text"
                      value={projectTitle}
                      onChange={(e) => setProjectTitle(e.target.value)}
                      onBlur={() => handleUpdateTitle(projectTitle)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateTitle(projectTitle);
                        if (e.key === 'Escape') {
                          setIsEditingTitle(false);
                          fetchProjectDetails(projectId.current);
                        }
                      }}
                      className={styles.projectTitleInput}
                      autoFocus
                    />
                  ) : (
                    <>
                      <h2 className={styles.projectTitle}>{projectTitle}</h2>
                      <button 
                        className={styles.editButton}
                        onClick={() => setIsEditingTitle(true)}
                        title={t('captionEditor.tooltips.editTitle')}
                      >
                        <FontAwesomeIcon style={{fontSize: "14px"}} icon={faPencilAlt} />
                      </button>
                      <DescriptionEditor
                        description={projectDescription}
                        onSave={handleUpdateDescription}
                        disabled={disabledBtnPendingDownload}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Right section - AI Caption Tools + Download link */}
          <section> 
            <div style={{ display: "flex", justifyContent: 'flex-end', alignItems: "center", gap: "1rem" }}>
              {video && (
                <>
                  {editMode === 'captions' && (
                    <Button 
                      className={styles.aiButton}
                      onClick={() => fetchCaptions(hasExistingCaptions)}
                      disabled={!video || disabledBtnPendingDownload}
                    >
                      {isProcessing.fetchingCaption ? 
                        <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span className={loaderStyles["lds-roller-min"]}><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></span>
                          {hasExistingCaptions ? t('captionEditor.buttons.regenerating') : t('captionEditor.buttons.generating')}
                        </span> :
                        hasExistingCaptions ? t('captionEditor.buttons.regenerateCaptions') : t('captionEditor.buttons.generateCaptions')
                      }
                    </Button>
                  )}

                  {currentItems && currentItems.length > 0 && (
                    <>
                      <Button 
                        className={styles.aiButton}
                        onClick={() => handleSaveCaptionsToS3(projectId.current, currentItems, editMode)}
                        disabled={!currentItems || disabledBtnPendingDownload}
                        style={{ 
                          background: "#2a2a2a",
                          color: "#ffffff",
                          border: "1px solid #404040"
                        }}
                      >
                        💾 {editMode === 'captions' ? t('captionEditor.buttons.saveCaptions') : t('captionEditor.buttons.saveTitles')}
                      </Button>

                      {(editMode === 'captions' && captions.length > 0) || (editMode === 'titles' && titles.length > 0) ? (
                        <Button 
                          className={styles.aiButton}
                          onClick={() => {
                            let allTextItems = getFilteredItemsForExport();
                            
                            // Add watermark caption if enabled
                            if (includeWatermark) {
                              const watermarkCaption = createWatermarkCaption();
                              allTextItems = [...allTextItems, watermarkCaption];
                            }
                            
                            handleSaveCaptionVideo({ videoPath: video, data: allTextItems, projectId: projectId.current});
                          }}
                          disabled={disabledBtnPendingDownload}
                          style={{ 
                            background: "linear-gradient(135deg, #8b5cf6, #ec4899)"
                          }}
                        >
                          {isProcessing.downloadCaptionVideo ?
                            <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              <span className={loaderStyles["lds-roller-min"]}><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></span>
                              {t('captionEditor.buttons.processing')}
                            </span> :
                            t('captionEditor.buttons.generateVideo')
                          }
                        </Button>
                      ) : null}
                    </>
                  )}
                </>
              )}
              
              {(downloadLink.current) &&  
                <a 
                  ref={refLinkDownload!}
                  className={styles.videoReadyBadge}
                  href={downloadLink.current } 
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  onClick={(e) => e.stopPropagation()}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </a> 
              }
            </div>
          </section>
        </Group>

        <Stack className={styles.layout}>
          {/* LEFT COLUMN - AI & Automation */}
          <div className={styles.left}>
            {/* Caption Editor Controls */}
            <CaptionEditorControls
              editMode={editMode}
              setEditMode={setEditMode}
              applyToAllCaptions={applyToAllCaptions}
              setApplyToAllCaptions={setApplyToAllCaptions}
              excludeCaptions={excludeCaptions}
              setExcludeCaptions={setExcludeCaptions}
              excludeTitles={excludeTitles}
              setExcludeTitles={setExcludeTitles}
              maxWordsPerCaption={maxWordsPerCaption}
              setMaxWordsPerCaption={setMaxWordsPerCaption}
              hasExistingCaptions={hasExistingCaptions}
              isRefetchingCaptions={isRefetchingCaptions}
              disabledBtnPendingDownload={disabledBtnPendingDownload}
              onRefetchCaptions={handleRefetchCaptions}
            />
            
            <div style={{
              backgroundColor: '#2a2a2a',
              border: '1px solid #444',
              borderRadius: '12px',
              padding: '16px'
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '12px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: '#b0b0b0'
                }}>
                  {t('captionEditor.outputFormat.title')}
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontSize: '16px' }}>📺</span>
                  <div style={{
                    padding: '10px 3px',
                    color: '#00d4aa',
                    fontSize: '14px',
                    fontWeight: '600',
                  }}>
                    {aspectRatio === '16:9' ? '16:9' : 
                     aspectRatio === '9:16' ? '9:16' : 
                     aspectRatio === '1:1' ? '1:1' : aspectRatio}
                  </div>
                  <div style={{
                    color: '#888',
                    fontSize: '12px',
                    whiteSpace: 'nowrap'
                  }}>
                    {aspectRatio === '16:9' ? t('captionEditor.outputFormat.horizontal') : 
                     aspectRatio === '9:16' ? t('captionEditor.outputFormat.vertical') : 
                     aspectRatio === '1:1' ? t('captionEditor.outputFormat.square') : ''}
                  </div>
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#888'
                }}>
                </div>
              </div>
            </div>

            {/* Watermark Checkbox - Always visible. Checked and disabled for free users, unchecked and enabled for paid users */}
            <div style={{
              backgroundColor: '#2a2a2a',
              border: '1px solid #444',
              borderRadius: '12px',
              padding: '12px',
              marginTop: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isFreePlan ? 0.7 : 1 // Slightly dimmed for free users to show it's disabled
            }}>
              <input
                type="checkbox"
                checked={includeWatermark}
                onChange={(e) => setIncludeWatermark(e.target.checked)}
                disabled={isFreePlan} // Disabled for free users
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#8b5cf6',
                  cursor: isFreePlan ? 'not-allowed' : 'pointer'
                }}
                id="watermarkCheckbox"
              />
              <label 
                htmlFor="watermarkCheckbox"
                style={{ 
                  color: '#e0e0e0', 
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isFreePlan ? 'not-allowed' : 'pointer',
                  userSelect: 'none'
                }}
              >
                {t('captionEditor.watermark.label')} {isFreePlan && <span style={{ color: '#888', fontSize: '12px' }}>{t('captionEditor.watermark.requiredForFree')}</span>}
              </label>
            </div>
          </div>

          {/* CENTER COLUMN - Video Player & Timeline */}
          <div className={styles.center}>
            {!video && (
              <div className={styles.noVideo} >
                {!disabledBtnPendingDownload && 
                  <div className={styles.noMedia}>
                      {t('captionEditor.videoPlayer.noVideo')}
                  </div>
                }
                {disabledBtnPendingDownload && 
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div className={loaderStyles["lds-roller"]}><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>
                    <div className={styles.noMedia}>
                      {t('captionEditor.videoPlayer.loadingVideo')}
                    </div>
                </div>
                }
              </div>
            )}

            {video && (
              <VideoPlayer
                mediaItems={[]}
                video={video}
                captions={displayItems}
                videoRef={videoRef}
                onTimeUpdate={handleTimeUpdate}
                onDurationChange={handleDurationChange}
                mediaCurrentTime={0}
                setMediaCurrentTime={() => {}}
                onVideoPlay={() => {}}
                onVideoPause={() => {}}
                onCaptionDrag={handleCaptionDrag}
                onCaptionClick={handleCaptionClick}
                aspectRatio={aspectRatio}
              />
            )}

            {video && (
              <div className={styles.timelineWrapper}>
                <VideoTimeline
                  duration={duration}
                  currentTime={currentTime}
                  segments={currentItems}
                  onSeek={handleSeek}
                  mediaItems={[]}
                  setMediaItems={() => {}}
                  audioUrl=""
                  setModalData={getModalData}
                  setSegments={setCurrentItems}
                />
              </div>
            )}
          </div>
          
          {/* Right Section – Editing Tools */}
          <div className={styles.right}>
            <EditSegmentTabs
              setCaptions={setCurrentItems}
              modalData={modalData}
              captions={currentItems}
              getCaptionId={getCaptionId}
              setMediaItems={() => {}}
              mediaItems={[]}
              onOpenAIVideoModal={() => {}}
              projectId={projectId.current}
              editMode={editMode}
              currentTime={currentTime}
              applyToAll={applyToAllCaptions}
            />
          </div>
        </Stack>
      </main>
    </div>
  );
};

export default CaptionEditor;